/**
 * LazadaTokensService — the one live token pair, in `LAZADA_Tokens` on the
 * CRM site.
 *
 * ON THE CRM SITE, for the same reason IGCOM_Orders is: Lazada is a CRM screen
 * and its list belongs beside B2B_Orders and IGCOM_Orders, on the site the
 * app's default `sp` client already points at. So it takes a CRM-style plain
 * name and provisions ITSELF from the screen rather than through the
 * Manufacturing site's Migration Valve.
 *
 * ── ONE ROW, REWRITTEN — NOT APPEND-ONLY ──────────────────────────────────
 * IGCOM_Orders and TIKTOK_Orders append chunks because they hold a history and
 * a half-finished sync must not be indistinguishable from a finished one.
 * This list holds a CREDENTIAL, and a credential has no history worth keeping:
 * every row but the newest is a dead token, and a list of dead tokens is a
 * list of things somebody might try. So there is one row and it is updated.
 *
 * ── ROTATION MAKES THE WRITE THE DANGEROUS PART ───────────────────────────
 * Refreshing ROTATES the refresh token: the old one dies the instant the new
 * one is issued. So if a refresh succeeds and storing its answer fails, the
 * pair we hold is already dead and the live one exists nowhere — the only way
 * back is a person re-authorising at the Lazada console.
 *
 * Which is why `save` is called with the answer BEFORE anything uses it, and
 * why a failure to save is raised loudly rather than swallowed. The usual
 * instinct — "carry on, we have a working token in memory" — is exactly wrong
 * here: carrying on spends the one refresh we can still account for.
 *
 * ── RESTRICT THIS LIST IN SHAREPOINT ──────────────────────────────────────
 * The refresh token is weak on its own — using it needs a signature, the
 * signature needs the app secret, and the app secret never leaves the Function
 * process. That is a real mitigation and it is not a permission model. Anybody
 * who can read the CRM site can read this list unless somebody breaks its
 * inheritance and restricts it, exactly as subconAccess gates a UI and not the
 * data behind it. Do that.
 */
import { sp } from "./spClient";
import { isAlreadyExistsError, isFieldMissingError, isListMissingError } from "./spErrors";
import { LAZADA_TOKENS_LIST, type ILazadaTokens } from "./lazadaTokenStore";

export * from "./lazadaTokenStore";

/**
 * The full read, and the pair it falls back to.
 *
 * CLAUDE.md's rule, learned here the hard way: SharePoint rejects the WHOLE
 * `$select` when it names one column the list does not have, so a half-created
 * list answers a read with "Field or property 'accessToken' does not exist" —
 * which reads exactly like the list itself being absent and was misreported as
 * such. `_CORE` is the columns every list has, so a read can always get far
 * enough to tell "no columns yet" apart from "no list".
 */
const TOKENS_SELECT =
  "Id,accessToken,refreshToken,accessExpiresAt,refreshExpiresAt,account,country,updatedBy,Modified";
const TOKENS_CORE = "Id,Modified";

interface IStoredRow {
  Id: number;
  accessToken?: string;
  refreshToken?: string;
  accessExpiresAt?: string;
  refreshExpiresAt?: string;
  account?: string;
  country?: string;
  updatedBy?: string;
  Modified?: string;
}

export interface ILazadaTokensRead {
  tokens: ILazadaTokens | null;
  /** The list does not exist yet — a STATE the screen offers to fix, not an
   *  error. Same posture as IgcomOrdersService.readStore. */
  missing: boolean;
}

export class LazadaTokensService {
  private typeName?: string;
  private rowId?: number;

  private async type(): Promise<string> {
    if (this.typeName) return this.typeName;
    const r = await sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${LAZADA_TOKENS_LIST}')?$select=ListItemEntityTypeFullName`);
    this.typeName = r.d.ListItemEntityTypeFullName;
    return this.typeName;
  }

  /**
   * The live pair, or null.
   *
   * Newest row wins if there is somehow more than one — a re-seed that raced
   * an update should not resurrect an older credential just because it sorts
   * first by Id.
   */
  public async read(): Promise<ILazadaTokensRead> {
    let rows: IStoredRow[];
    try {
      rows = await sp.getListItems<IStoredRow>(LAZADA_TOKENS_LIST, { select: TOKENS_SELECT });
    } catch (e) {
      // ORDER MATTERS. A missing COLUMN and a missing LIST both say "does not
      // exist", so the narrower question is asked first — otherwise a list
      // created without its columns is reported as absent, the screen offers
      // to create it, and SharePoint refuses because it is already there.
      if (isFieldMissingError(e)) {
        // The list exists but is not fully provisioned. Confirm it is reachable
        // with a select that cannot fail, then say "no columns" by reporting no
        // tokens — provision() is idempotent and will finish the job.
        await sp.getListItems<IStoredRow>(LAZADA_TOKENS_LIST, { select: TOKENS_CORE });
        return { tokens: null, missing: true };
      }
      if (isListMissingError(e)) return { tokens: null, missing: true };
      throw e;
    }

    const newest = [...rows].sort(
      (a, b) => String(b.Modified ?? "").localeCompare(String(a.Modified ?? "")) || b.Id - a.Id,
    )[0];
    if (!newest || !newest.refreshToken) return { tokens: null, missing: false };

    this.rowId = newest.Id;
    return {
      missing: false,
      tokens: {
        accessToken: newest.accessToken ?? "",
        refreshToken: newest.refreshToken ?? "",
        accessExpiresAt: newest.accessExpiresAt ?? "",
        refreshExpiresAt: newest.refreshExpiresAt ?? "",
        account: newest.account ?? "",
        country: newest.country ?? "",
        updatedAt: newest.Modified ?? "",
        updatedBy: newest.updatedBy ?? "",
      },
    };
  }

  /**
   * Store a pair, replacing whatever was there.
   *
   * Never swallows a failure. See the header: after a rotation the answer we
   * are being asked to store is the ONLY live credential, and "carry on, it
   * works in memory" spends it with nowhere to write the next one.
   */
  public async save(tokens: ILazadaTokens): Promise<void> {
    const type = await this.type();
    const body = {
      __metadata: { type },
      Title: tokens.account || "Lazada",
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      accessExpiresAt: tokens.accessExpiresAt,
      refreshExpiresAt: tokens.refreshExpiresAt,
      account: tokens.account,
      country: tokens.country,
      updatedBy: tokens.updatedBy,
    };

    // Find the row to overwrite if this instance has not seen it yet — a page
    // that refreshed between reads must not create a second credential row.
    if (this.rowId === undefined) await this.read();

    if (this.rowId === undefined) {
      const created = await sp.createItem<{ Id: number }>(LAZADA_TOKENS_LIST, body);
      this.rowId = created?.Id;
      return;
    }

    await sp.raw(`/_api/web/lists/getbytitle('${LAZADA_TOKENS_LIST}')/items(${this.rowId})`, {
      method: "POST",
      headers: { "X-HTTP-Method": "MERGE", "If-Match": "*" },
      body: JSON.stringify(body),
    });
  }

  /**
   * Create the list on the CRM site. Explicit user action only.
   *
   * IT IS IDEMPOTENT, AND THAT IS NOT A NICETY. The first version was not, and
   * its first run half-succeeded: the list was created, every column was
   * REFUSED, and pressing the button again failed on "a list with the
   * specified title already exists" — so the one action offered to fix the
   * problem was the one action that could not run. Provisioning is a sequence
   * of writes against a remote service; any step can fail, so every step has
   * to be safe to repeat.
   *
   * THE TOKEN COLUMNS ARE `note`, NOT `text`, AND THAT IS WHY IT FAILED.
   * SharePoint's single-line text caps at 255 characters and refuses a field
   * asking for more — this asked for 512, because Lazada's tokens are long.
   * A note column has no such cap. The cost is cosmetic: list views render it
   * as a wrapped block. That is a poor reason to store a credential in a
   * column that cannot hold it.
   */
  public async provision(onProgress?: (m: string) => void): Promise<void> {
    onProgress?.(`Creating ${LAZADA_TOKENS_LIST}…`);
    try {
      await sp.raw(`/_api/web/lists`, {
      method: "POST",
      body: JSON.stringify({
        __metadata: { type: "SP.List" },
        Title: LAZADA_TOKENS_LIST,
        Description:
          "CRM · Lazada — the live OAuth token pair for the i-Synergy Lazada app. ONE ROW, "
          + "rewritten: Lazada's refresh token ROTATES, so every earlier row is a dead credential. "
          + "The access token lasts 7 days and the refresh token about 30, renewed automatically "
          + "whenever the Lazada screen is opened. RESTRICT THIS LIST — break inheritance and limit "
          + "it to the people who run the integration. Read and written by the Lazada screen only.",
        BaseTemplate: 100,
      }),
      });
    } catch (e) {
      // Already there from a run that got this far and then failed. Carry on
      // to the columns — that is almost certainly what is missing.
      if (!isAlreadyExistsError(e)) throw e;
      onProgress?.(`${LAZADA_TOKENS_LIST} already exists — checking its columns…`);
    }

    /**
     * `note` for the two tokens because they exceed 255 characters, `text` for
     * the rest because they do not. The kinds are SharePoint's: 2 = single
     * line of text, 3 = multiple lines.
     */
    const fields: { name: string; kind: 2 | 3 }[] = [
      { name: "accessToken", kind: 3 },
      { name: "refreshToken", kind: 3 },
      { name: "accessExpiresAt", kind: 2 },
      { name: "refreshExpiresAt", kind: 2 },
      { name: "account", kind: 2 },
      { name: "country", kind: 2 },
      { name: "updatedBy", kind: 2 },
    ];
    const url = `/_api/web/lists/getbytitle('${LAZADA_TOKENS_LIST}')/fields`;
    for (const f of fields) {
      onProgress?.(`Adding ${f.name}…`);
      try {
        await sp.raw(url, {
          method: "POST",
          body: JSON.stringify({
            // The __metadata TYPE has to match the properties being set.
            // `RichText` belongs to SP.FieldMultiLineText, and sending it
            // against a plain SP.Field is refused outright: "The property
            // 'RichText' does not exist on type 'SP.Field'". SharePoint
            // validates the declared type, not the FieldTypeKind.
            __metadata: { type: f.kind === 3 ? "SP.FieldMultiLineText" : "SP.Field" },
            Title: f.name,
            FieldTypeKind: f.kind,
            Required: false,
            // Rich text on a note column stores markup AROUND the value and
            // hands it back wrapped in it — a token in a <div> is not a token.
            ...(f.kind === 3 ? { RichText: false, NumberOfLines: 4 } : {}),
          }),
        });
      } catch (e) {
        if (!isAlreadyExistsError(e)) throw e;
      }
    }

    this.typeName = undefined;
    this.rowId = undefined;
    onProgress?.(`${LAZADA_TOKENS_LIST} ready (${fields.length} columns) — now restrict it`);
  }
}

export const lazadaTokensService = new LazadaTokensService();
